import React, { useEffect, useRef, useMemo } from 'react';
import { useMount } from '@shared/utils/hooks/useMount';
import { useHotelBookingGuestSiteTelemetry } from '../../HotelBooking.telemetry';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { useHotelSearchParams } from './useHotelSearchParams';
import { config } from '@static/js/env.config';
import { useHotelBookingRedirectHealth, wrapWithHotelBookingRedirectProvider } from '../../HotelBooking.health';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { AccommodationsConsoleConfig, AccommodationsConsoleConfigProvider, MapLayoutMode } from '@apps/accommodations/AccommodationsConsoleConfigContext';
import { AccommodationsConsoleDialog } from '@apps/accommodations/AccommodationsConsole';
import { SortOption } from '@apps/accommodations/AccommodationsConsole.types';
import { useTranslation } from '@shared/core';
import { useEventDisplayName } from '@shared/utils/eventInfo/useEventDisplayName';

export const HotelSearch = wrapWithHotelBookingRedirectProvider(({ eventHandle }: { eventHandle: string }) => {
  const redirectInProgress = useRef(false);
  const { onLoadFailure, onLoadComplete } = useHotelBookingRedirectHealth();
  const telemetry = useHotelBookingGuestSiteTelemetry();
  const { tq } = useTranslation('accommodationsConsole');
  const eventDisplayName = useEventDisplayName();

  const [isJoyHotelSearchPageEnabled, isFeatureFlagLoading] = useIsFeatureEnabled('joyHotelSearchPage');
  const { loading, buildRedirectUrl, ...restParams } = useHotelSearchParams(eventHandle);

  useMount(() => {
    telemetry.hotelSearch.enter();
  });

  const consoleConfig = useMemo<AccommodationsConsoleConfig>(
    () => ({
      topBar: {
        searchForm: {
          dateRangeEnabled: true,
          occupancySelectorEnabled: true,
        },
        showCloseButton: false,
        showJoyLogo: true,
      },
      layout: {
        mapLayout: {
          mode: MapLayoutMode.COMPACT,
        },
        skipCouplePicks: true,
      },
      map: {
        shouldShowVenueMapPin: false,
      },
      filtersPanel: {
        hideEventNameAndNote: true,
        shouldShowJoyTravelBenefitsFilter: false,
      },
      listingsPanel: {
        listingData: {
          shouldShowAllAccommodations: false,
          shouldFilterOutNonJoyRoomBlocks: false,
        },
        sortConfig: {
          sortOptions: [
            [SortOption.TOP_MATCHES, tq`Top Matches`],
            [SortOption.PRICE_LOW_TO_HIGH, tq`Price Low to High`],
            [SortOption.PRICE_HIGH_TO_LOW, tq`Price High to Low`],
            [SortOption.MOST_SAVINGS, tq`Most Savings`],
            [SortOption.DISTANCE, tq`Distance`],
          ],
          defaultSortOption: SortOption.TOP_MATCHES,
        },
        listingEntry: {
          roomBlockTag: '',
          shouldShowCouplePicksTag: false,
          shouldShowAddedTag: false,
          shouldShowRoomBlockNoteAndDate: false,
          bookingCta: {
            default: {
              buttonTitle: tq`Book on Joy`,
              buttonProps: {
                variant: 'solid',
                intent: 'newPrimary',
                _hover: { backgroundColor: { _: 'newUIBrandPrimary', md: 'buttonNewPrimaryHover' } },
              },
            },
          },
        },
      },
    }),
    [tq],
  );

  // Call onLoadComplete when showing AccommodationsConsole to prevent timeout
  useEffect(() => {
    if (!isFeatureFlagLoading && isJoyHotelSearchPageEnabled) {
      onLoadComplete();
    }
  }, [isFeatureFlagLoading, isJoyHotelSearchPageEnabled, onLoadComplete]);

  // Old redirect logic (control)
  useEffect(() => {
    if (isFeatureFlagLoading || isJoyHotelSearchPageEnabled || redirectInProgress.current || loading) {
      return;
    }

    try {
      /**
       * Try redirect to Nuitee
       */
      const nuiteeRedirectUrl = buildRedirectUrl({
        url: config.nuiteeWhitelableBaseUrl,
        fields: ['clientReference', 'placeId', 'name', 'checkin', 'checkout', 'currency', 'language', 'xData'],
      });

      telemetry.partnerHotelSearchRedirect({
        destinationName: 'stays',
        destinationUrl: nuiteeRedirectUrl,
        clientReference: restParams.clientReference,
        locationPlaceId: restParams.placeId,
        locationName: restParams.name,
        locationSource: restParams.locationSource,
        checkin: restParams.checkin,
        checkout: restParams.checkout,
        utm_source: restParams.utm_source,
        utm_source_secondary: restParams.utm_source_secondary,
        utm_medium: restParams.utm_medium,
        utm_campaign: restParams.utm_campaign,
        onTelemetrySend: () => {
          onLoadComplete({ url: nuiteeRedirectUrl, isExternalUrl: true });
        },
      });

      redirectInProgress.current = true;
    } catch (error) {
      onLoadFailure(`HotelSearch: Failed to build redirect URL`, error as Error, { url: config.nuiteeWhitelableBaseUrl, isExternalUrl: true });
      redirectInProgress.current = true;
    }
  }, [buildRedirectUrl, loading, onLoadComplete, onLoadFailure, restParams, telemetry, isFeatureFlagLoading, isJoyHotelSearchPageEnabled]);

  // Show loader while feature flag is loading
  if (isFeatureFlagLoading) {
    return <JoyLogoLoader loaderKey="hotel-search" />;
  }

  // New AccommodationsConsole implementation (treatment)
  if (isJoyHotelSearchPageEnabled) {
    return (
      <AccommodationsConsoleConfigProvider config={consoleConfig}>
        <AccommodationsConsoleDialog
          isOpen={true}
          eventHandle={eventHandle}
          eventName={eventDisplayName}
          viewMode="standalone"
          shouldPrioritizeParams={true}
        />
      </AccommodationsConsoleConfigProvider>
    );
  }

  // Old redirect logic (control) - show loader until redirect starts
  return <>{!redirectInProgress.current && <JoyLogoLoader loaderKey="hotel-search" />}</>;
});
